import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  LabelHTMLAttributes,
  ReactNode,
} from 'react'

import { useTheme } from '../theme/ThemeContext'
import {
  chromeInputClass,
  chromePrimaryClass,
  chromePrimaryCompactClass,
  type ChromePrimaryShape,
  chromeToolbarButtonClass,
  chromeToolbarDangerClass,
} from './ChromeSurface'

type ChromeToolbarButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode
}

/** 顶栏次要操作（与 chromeToolbarButtonClass 一致，主题由内层统一读取） */
export function ChromeToolbarButton({
  children,
  className = '',
  ...rest
}: ChromeToolbarButtonProps) {
  const { theme } = useTheme()
  const angular = theme === 'pixel' || theme === 'geek'
  return (
    <button
      type="button"
      className={[chromeToolbarButtonClass(angular), className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </button>
  )
}

export function ChromeToolbarDangerButton({
  children,
  className = '',
  ...rest
}: ChromeToolbarButtonProps) {
  const { theme } = useTheme()
  const angular = theme === 'pixel' || theme === 'geek'
  return (
    <button
      type="button"
      className={[chromeToolbarDangerClass(angular), className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </button>
  )
}

type ChromePrimaryUploadLabelProps = Omit<
  LabelHTMLAttributes<HTMLLabelElement>,
  'onChange' | 'children'
> & {
  children?: ReactNode
  onFiles?: (files: FileList | null) => void
  accept?: string
  multiple?: boolean
  /** 为 true 时阻断选择文件并呈现与主按钮一致的禁用态 */
  disabled?: boolean
  /** 选文件夹批量上传（与 {@link ChromeToolbarUploadLabel} 一致） */
  directory?: boolean
}

type ChromeTextInputProps = InputHTMLAttributes<HTMLInputElement>

export function ChromeTextInput({ className = '', ...rest }: ChromeTextInputProps) {
  const { theme } = useTheme()
  const angular = theme === 'pixel' || theme === 'geek'
  return (
    <input
      className={[chromeInputClass(angular), className].filter(Boolean).join(' ')}
      {...rest}
    />
  )
}

/** 主色上传按钮（label + 隐藏 file input），与图库等处「上传图片」一致 */
type ChromePrimaryButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  children: ReactNode
  /** compact：弹窗/MOD 内等小主按钮；默认与「上传图片」等高 */
  density?: 'default' | 'compact'
  /** pill：胶囊圆角（默认）；rounded：较小圆角 */
  shape?: ChromePrimaryShape
}

/** 实心主按钮（accent），统一样式，避免页面散落 className */
export function ChromePrimaryButton({
  children,
  className = '',
  density = 'default',
  shape = 'pill',
  type = 'button',
  ...rest
}: ChromePrimaryButtonProps) {
  const { theme } = useTheme()
  const angular = theme === 'pixel' || theme === 'geek'
  const base =
    density === 'compact'
      ? chromePrimaryCompactClass(angular, shape)
      : chromePrimaryClass(angular, { shape })
  return (
    <button
      type={type}
      className={[base, className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </button>
  )
}

type ChromeToolbarUploadLabelProps = Omit<
  LabelHTMLAttributes<HTMLLabelElement>,
  'onChange' | 'children'
> & {
  children?: ReactNode
  onFiles?: (files: FileList | null) => void
  accept?: string
  multiple?: boolean
  disabled?: boolean
  /** 选择本地文件夹，返回其下（递归）的文件列表；各浏览器略有差异 */
  directory?: boolean
}

/** 顶栏/工具栏式上传（outline），支持多选文件或 webkitdirectory 整夹选取 */
export function ChromeToolbarUploadLabel({
  children,
  onFiles,
  accept = 'image/*',
  multiple = true,
  disabled = false,
  directory = false,
  className = '',
  ...rest
}: ChromeToolbarUploadLabelProps) {
  const { theme } = useTheme()
  const angular = theme === 'pixel' || theme === 'geek'
  return (
    <label
      aria-disabled={disabled || undefined}
      className={[
        chromeToolbarButtonClass(angular),
        disabled ? 'pointer-events-none cursor-not-allowed opacity-50 saturate-75' : 'cursor-pointer',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      <input
        type="file"
        {...(directory
          ? { webkitdirectory: '', directory: '', multiple: true }
          : {
              accept,
              multiple,
            })}
        disabled={disabled}
        className="sr-only"
        onChange={(e) => {
          onFiles?.(e.target.files)
          e.target.value = ''
        }}
      />
      {children}
    </label>
  )
}

export function ChromePrimaryUploadLabel({
  children = '上传图片',
  onFiles,
  accept = 'image/*',
  multiple = true,
  disabled = false,
  directory = false,
  className = '',
  ...rest
}: ChromePrimaryUploadLabelProps) {
  const { theme } = useTheme()
  const angular = theme === 'pixel' || theme === 'geek'
  return (
    <label
      aria-disabled={disabled || undefined}
      className={[
        chromePrimaryClass(angular),
        disabled ? 'pointer-events-none cursor-not-allowed opacity-50 saturate-75' : 'cursor-pointer',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      <input
        type="file"
        {...(directory ? { webkitdirectory: '', directory: '', multiple: true } : { accept, multiple })}
        disabled={disabled}
        className="sr-only"
        onChange={(e) => {
          onFiles?.(e.target.files)
          e.target.value = ''
        }}
      />
      {children}
    </label>
  )
}
